import { reportLeadEmailFailure, type LeadEmailContext } from "./leadEmails.functions";

/**
 * Reliable, non-blocking delivery of lead notification emails.
 *
 * Contract for every public lead form:
 *   1. The database row is the source of truth. If the insert succeeded the
 *      visitor sees success, full stop — a mail-provider hiccup must never
 *      make them think the lead was lost.
 *   2. Email failures are therefore never surfaced to the customer.
 *   3. But they are never silent either: per-recipient failures are already
 *      written to `email_send_log` by dispatch.server, and a *transport*
 *      failure (the send request never reached the server) is recorded here
 *      so it shows up on the admin Email Log / Email Health screens.
 *
 * Sends are idempotency-keyed per lead, so the retry below can never produce
 * a duplicate email.
 */

const RETRY_DELAY_MS = 1_500;

export interface DeliverLeadEmailsOptions {
  /** Which form triggered the send — used for the admin log entry. */
  context: LeadEmailContext;
  /** The row id of the lead that was already saved. */
  leadId: string;
  /** Called to perform the send. Must be safe to call twice. */
  send: () => Promise<unknown>;
  /** Overridable for tests. */
  retryDelayMs?: number;
}

/** Never throws, never rejects. Safe to `void`. */
export async function deliverLeadEmails(options: DeliverLeadEmailsOptions): Promise<boolean> {
  const { context, leadId, send } = options;
  const delay = options.retryDelayMs ?? RETRY_DELAY_MS;

  try {
    await send();
    return true;
  } catch (firstError) {
    console.error(`[lead-email] ${context} notification failed, retrying`, firstError);
  }

  if (delay > 0) {
    await new Promise((resolve) => setTimeout(resolve, delay));
  }

  try {
    await send();
    return true;
  } catch (finalError) {
    const message = finalError instanceof Error ? finalError.message : String(finalError);
    console.error(`[lead-email] ${context} notification failed after retry`, message);
    try {
      await reportLeadEmailFailure({
        data: { context, leadId, message: message.slice(0, 500) },
      });
    } catch (reportError) {
      // Best effort only — the lead itself is already safely stored.
      console.error("[lead-email] could not record the delivery failure", reportError);
    }
    return false;
  }
}
